import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Briefcase, Github, Globe, Instagram, Linkedin, MessageCircle, MessageSquare, Phone, Twitter } from 'lucide-react';
import { roleDates } from './contentFormat';

// Home page cards. One responsive tree for every screen size; on desktop only cards of similar
// natural height share a row (About + Tech beside Experience, Certifications + Connect).

export const cardClass = (darkMode, extra = '') =>
  `${extra} p-4 sm:p-6 rounded-2xl border transition-all duration-300 hover:shadow-lg ${
    darkMode ? 'bg-[#111] border-gray-800 hover:border-gray-700' : 'bg-white border-gray-300 hover:border-gray-400'}`;

const Title = ({ darkMode, icon, children, action }) => (
  <div className="flex items-center justify-between gap-2 mb-3 sm:mb-4">
    <div className="flex items-center gap-2">
      <span className={darkMode ? 'text-gray-400' : 'text-gray-600'} aria-hidden="true">{icon}</span>
      <h2 className={`text-lg leading-tight font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{children}</h2>
    </div>
    {action}
  </div>
);

const ViewAll = ({ to, darkMode, children = 'View All →' }) => (
  <Link to={to} className={`-my-3 text-sm font-medium transition-colors ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>
    {children}
  </Link>
);

const PersonIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);
const CodeIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
  </svg>
);
const LinkIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
  </svg>
);
const FacebookIcon = () => (
  <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

// ---------------------------------------------------------------- About (+ Beyond coding)

export const AboutCard = ({ darkMode, profile, className = '' }) => (
  <section className={cardClass(darkMode, className)} aria-labelledby="about-h">
    <Title darkMode={darkMode} icon={<PersonIcon />}><span id="about-h">About</span></Title>
    <div className={`-mb-1 space-y-2 sm:space-y-3 text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
      {profile.about.map((p, i) => <p key={i}>{p}</p>)}
    </div>
  </section>
);

const BookIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
  </svg>
);

export const BeyondCard = ({ darkMode, profile, className = '' }) => (
  <section className={cardClass(darkMode, className)} aria-labelledby="beyond-h">
    <div className="flex items-center gap-2 mb-3">
      <span className={darkMode ? 'text-gray-400' : 'text-gray-600'} aria-hidden="true"><BookIcon /></span>
      <h2 id="beyond-h" className={`text-lg leading-tight font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Beyond Coding</h2>
    </div>
    <p className={`-mb-1 text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{profile.beyond_coding}</p>
  </section>
);

// ---------------------------------------------------------------- Experience (original timeline design)

// Same markup and spacing as the pre-2026-10 portfolio. On desktop the card is as tall as the left column (the
// wrapper's [contain:size] keeps the roles from setting the row height); the availability note sits at the bottom
// and the timeline scrolls above it once there are more roles than fit. On phones everything shows in full.
// Home-card tags picked by Christian (2026-10-12), by company; any other role shows its first platform from /admin.
// The full platform lists stay in /admin and on the resume.
const HOME_TAGS = {
  'BCP Holdings': ['WordPress', 'SEO', 'AEO'],
  'The Business Connect Hub': ['Shopify'],
  'The Aeternum Company': ['WooCommerce'],
  'Bony Levy': ['BigCommerce'],
  'Kashmer Interactive, LLC': ['WordPress'],
  'BlueSpot Advertising, LLC': ['GoHighLevel'],
  'MyCreativePanda': ['CakePHP', 'PHP'],
  'MGHS Services': ['WordPress', 'SEO'],
};

export const ExperienceCard = ({ darkMode, roles, className = '' }) => {
  const list = roles.map((r) => ({ id: r.id, title: r.title, company: r.company,
    tags: HOME_TAGS[r.company] || (r.platforms.length ? r.platforms.slice(0, 1) : [roleDates(r)].filter(Boolean)) }));
  return (
    <div className={`lg:[contain:size] ${className}`}>
    <section className={cardClass(darkMode, 'flex flex-col lg:h-full')} aria-labelledby="exp-h">
      <div className="flex items-center gap-2 mb-4 sm:mb-6">
        <Briefcase size={18} className={`flex-shrink-0 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
        <h2 id="exp-h" className={`text-lg leading-tight font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Experience</h2>
      </div>
      <div className="lg:flex-1 lg:min-h-0 lg:flex lg:flex-col">
        <div className="lg:flex-1 lg:flex lg:flex-col lg:min-h-0 lg:overflow-y-auto lg:overflow-x-hidden overscroll-contain thin-scroll lg:-mr-4 lg:pr-4" tabIndex={0} aria-label="Experience timeline">
          <div className="lg:flex-1 lg:flex lg:flex-col">
            {list.map((exp, idx) => (
              <div key={exp.id} className="relative group lg:flex-grow">
                {/* Connecting Line */}
                <div className={`absolute left-[7px] top-[14px] w-0.5 ${
                  idx === list.length - 1 ? 'h-0' : 'h-full'
                } ${darkMode ? 'bg-gray-800' : 'bg-gray-300'}`}></div>

                <div className={`flex items-start gap-3 relative pb-4`}>
                  {/* Circle Indicator */}
                  <div className="relative flex-shrink-0 mt-1.5 z-10">
                    <div className={`w-4 h-4 rounded-full border-2 transition-all duration-300 ${
                      idx === 0
                        ? (darkMode ? 'bg-white border-white' : 'bg-gray-900 border-gray-900')
                        : (darkMode
                            ? 'bg-[#111] border-gray-600 group-hover:bg-white group-hover:border-white'
                            : 'bg-white border-gray-400 group-hover:bg-gray-900 group-hover:border-gray-900')
                    }`}></div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h3 className={`font-bold text-sm mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{exp.title}</h3>
                    <div className="flex justify-between items-start gap-2">
                      <p className={`flex-1 min-w-0 line-clamp-2 text-[0.8125rem] font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{exp.company}</p>
                      {/* one pill per platform, always on one row */}
                      <span className="flex flex-shrink-0 gap-1">
                        {exp.tags.map((t) => (
                          <span key={t} className={`text-[0.6875rem] whitespace-nowrap px-2 py-0.5 rounded-md font-medium ${
                            darkMode ? 'bg-gray-800/50 text-gray-400 border border-gray-700/50' : 'bg-gray-50 text-gray-600 border border-gray-200'
                          }`}>{t}</span>
                        ))}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Availability: black-and-white like the Download Resume button (inverted in dark mode).
          Compact on phones and tablets (one detail line), roomier beside the desktop timeline. */}
      <div className={`shine ${darkMode ? 'shine-dark' : ''} mt-0 rounded-xl p-4 lg:px-3.5 lg:py-3 flex items-center gap-3 ${darkMode ? 'bg-white text-gray-900' : 'bg-gray-900 text-white'}`}>
        <div className={`relative flex-shrink-0 w-8 h-8 lg:w-10 lg:h-10 rounded-lg flex items-center justify-center ${darkMode ? 'bg-gray-900/5' : 'bg-white/10'}`}>
          <Globe className="w-4 h-4 lg:w-5 lg:h-5" aria-hidden="true" />
          <span className="absolute bottom-0.5 right-0.5 lg:bottom-1 lg:right-1 flex h-1.5 w-1.5 lg:h-2 lg:w-2" aria-hidden="true">
            <span className={`absolute inline-flex h-full w-full rounded-full opacity-60 motion-safe:animate-ping ${darkMode ? 'bg-gray-900' : 'bg-white'}`} />
            <span className={`relative inline-flex h-full w-full rounded-full ${darkMode ? 'bg-gray-900' : 'bg-white'}`} />
          </span>
        </div>
        <div className="min-w-0">
          <p className="text-sm lg:text-[0.9375rem] font-semibold leading-tight">Open to remote work</p>
          <p className={`mt-0.5 lg:mt-1 text-xs leading-snug whitespace-nowrap lg:whitespace-normal truncate lg:overflow-visible ${darkMode ? 'text-gray-600' : 'text-white/70'}`}>
            <span className="lg:hidden">Full-time &amp; projects · US/UK/AU hours</span>
            <span className="hidden lg:inline">Full-time &amp; projects<br />GMT+8 · US, UK &amp; AU hours</span>
          </p>
        </div>
      </div>
    </section>
    </div>
  );
};

// ---------------------------------------------------------------- Tech stack (original design)

// Home Tech Stack: hand-picked tools for a WordPress/Shopify developer, taken by name from any /admin group.
// Lists are in priority order; each group then shows as many whole chips as fit its row limit:
// one row on desktop, two rows below 1024px. Phones use slightly tighter chips so the 5 platforms fit 2 rows.
const GROUPS = [
  ['E-Commerce & CMS', 'max-h-[4.5rem] lg:max-h-8', ['Shopify', 'WordPress', 'WooCommerce', 'BigCommerce', 'GoHighLevel']],
  ['Frontend', 'max-h-[4.5rem] lg:max-h-8', ['HTML', 'CSS', 'JavaScript', 'React', 'Next.js', 'Tailwind CSS',
    'PWA', 'Bootstrap', 'Preact', 'Angular JS']],
  ['Search & Marketing Automation', 'max-h-[4.5rem] lg:max-h-8', ['SEO', 'AEO', 'Klaviyo', 'ChatGPT', 'Claude',
    'Google Analytics', 'Search Console', 'Google Tag Manager']],
];
const pick = (skills, names) => {
  const all = Object.values(skills).flat();
  return names.map((n) => all.find((x) => x.name === n)).filter(Boolean);
};

export const TechCard = ({ darkMode, skills, className = '' }) => (
  <section className={cardClass(darkMode, className)} aria-labelledby="tech-h">
    <Title darkMode={darkMode} icon={<CodeIcon />} action={<ViewAll to="/tech-stack" darkMode={darkMode} />}>
      <span id="tech-h">Tech Stack</span>
    </Title>
    <div className="space-y-3 sm:space-y-4">
      {GROUPS.map(([label, rows, names]) => (
        <div key={label}>
          <h3 className={`text-xs font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{label}</h3>
          <div className={`flex flex-wrap gap-x-1.5 gap-y-2 sm:gap-2 lg:gap-x-1.5 xl:gap-x-2 overflow-hidden ${rows}`}>
            {pick(skills, names).map((skill) => (
              <span key={skill.name} className={`flex items-center gap-1 min-[375px]:gap-1.5 h-8 px-1.5 min-[375px]:px-2 sm:px-3 lg:px-2.5 xl:px-3 text-[0.6875rem] min-[375px]:text-xs font-medium rounded-lg border whitespace-nowrap transition-colors duration-300 ${
                darkMode ? 'bg-gray-900/50 border-gray-800 text-gray-300 hover:border-gray-700 hover:bg-gray-900' : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-white'
              }`}>
                {skill.logo && (
                  <img src={skill.logo} alt="" width={16} height={16} loading="lazy" className="w-3.5 h-3.5"
                    style={{ filter: darkMode ? 'grayscale(100%) brightness(1.5) contrast(0.9)' : 'grayscale(100%) brightness(0.5) contrast(1.1)' }} />
                )}
                {skill.name}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  </section>
);

// ---------------------------------------------------------------- Certifications

export const CertsCard = ({ darkMode, certifications, className = '' }) => (
  <section className={cardClass(darkMode, className)} aria-labelledby="cert-h">
    <Title darkMode={darkMode} icon={<Award size={18} />} action={<ViewAll to="/certifications" darkMode={darkMode} />}>
      <span id="cert-h">Certifications</span>
    </Title>
    <ul className="grid sm:grid-cols-2 gap-2 sm:gap-3">
      {certifications.map((cert, i) => (
        <li key={cert.title} className={`p-3 rounded-xl border ${i >= 4 ? 'hidden sm:block ' : ''}${
          i === certifications.length - 1 && certifications.length % 2 ? 'sm:col-span-2 ' : ''}${
          darkMode ? 'bg-black border-gray-800' : 'bg-gray-50 border-gray-200'}`}>
          <h3 className={`font-bold text-sm mb-0.5 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{cert.title}</h3>
          <p className={`text-[0.8125rem] ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{cert.issuer}</p>
        </li>
      ))}
    </ul>
  </section>
);

// ---------------------------------------------------------------- Connect (social profiles)

const SOCIAL_ICONS = {
  LinkedIn: <Linkedin size={16} aria-hidden="true" />, GitHub: <Github size={16} aria-hidden="true" />,
  Facebook: <FacebookIcon />, Instagram: <Instagram size={16} aria-hidden="true" />,
  'Twitter / X': <Twitter size={16} aria-hidden="true" />, WhatsApp: <MessageCircle size={16} aria-hidden="true" />,
  Viber: <Phone size={16} aria-hidden="true" />, Discord: <MessageSquare size={16} aria-hidden="true" />,
};
const FIRST = ['LinkedIn', 'GitHub'];   // most relevant to clients, then the order set in /admin

export const ConnectCard = ({ darkMode, profile, onSocial, className = '' }) => {
  const links = (profile.socials || [])
    .filter((x) => x.visible !== false && x.url && SOCIAL_ICONS[x.name])
    .sort((a, b) => (FIRST.includes(b.name) - FIRST.includes(a.name)) || FIRST.indexOf(a.name) - FIRST.indexOf(b.name))
    .slice(0, 6);
  return (
    <section className={cardClass(darkMode, `flex flex-col ${className}`)} aria-labelledby="connect-h">
      <Title darkMode={darkMode} icon={<LinkIcon />} action={<ViewAll to="/social-links" darkMode={darkMode} />}>
        <span id="connect-h">Connect</span>
      </Title>
      <ul className="flex-1 grid grid-cols-2 auto-rows-fr gap-2 sm:gap-3">
        {links.map((x) => (
          <li key={x.name}>
            <a href={x.url} target="_blank" rel="noopener noreferrer" onClick={() => onSocial(x.name)}
              className={`h-full flex items-center gap-2.5 p-3 rounded-xl border transition-colors ${
                darkMode ? 'bg-black border-gray-800 hover:border-gray-600' : 'bg-gray-50 border-gray-200 hover:border-gray-400'}`}>
              <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{SOCIAL_ICONS[x.name]}</span>
              <span className="min-w-0">
                <span className={`block text-xs font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{x.name.replace('Twitter / X', 'X')}</span>
                <span className={`block text-xs truncate ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{x.type || x.username}</span>
              </span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
};

// ---------------------------------------------------------------- Reel (video highlights, /admin > Profile > Reel)

// The profile photo doubles as the reel's play button (a story-style ring and a "Reel" tag).
// Nothing extra loads with the page; the video downloads when someone opens it.
export const ReelPhoto = ({ darkMode, reel, onPlay, className = '', children }) => {
  const [open, setOpen] = React.useState(false);
  const button = React.useRef(null);
  // ?reel=1 (the link in Job Hunter letters) opens it on arrival. Only the visible photo opens it:
  // the page has one photo for phones and one for desktop.
  React.useEffect(() => {
    if (new URLSearchParams(window.location.search).has('reel') && button.current?.offsetParent) {
      setOpen(true);
      onPlay?.('Letter link');
    }
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (!open) return undefined;
    const close = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', close, true);   // capture: the video's own controls would swallow Escape
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', close, true); document.body.style.overflow = ''; };
  }, [open]);
  if (!reel?.video) return children;
  return (
    <>
      <button type="button" ref={button} onClick={() => { setOpen(true); onPlay?.(); }}
        aria-label={`Play my ${reel.duration ? `${reel.duration}-second ` : ''}video reel`}
        className={`group relative flex flex-shrink-0 rounded-2xl ring-2 ring-offset-2 transition-shadow ${
          darkMode ? 'ring-white ring-offset-black' : 'ring-gray-900 ring-offset-white'} ${className}`}>
        {children}
        <span className={`absolute left-1/2 bottom-0 -translate-x-1/2 translate-y-1/2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full
          text-[11px] font-semibold whitespace-nowrap border-2 transition-transform group-hover:scale-105 ${
          darkMode ? 'bg-white text-black border-black' : 'bg-gray-900 text-white border-white'}`}>
          <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
          Reel
        </span>
      </button>
      {open && (
        <div role="dialog" aria-modal="true" aria-label={reel.title || 'Video reel'} onClick={() => setOpen(false)}
          className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/85">
          <button type="button" onClick={() => setOpen(false)} aria-label="Close video"
            className="absolute top-3 right-3 w-11 h-11 flex items-center justify-center rounded-full bg-neutral-800 hover:bg-neutral-700 border border-white/15 text-white transition-colors">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
          <video src={reel.video} poster={reel.poster} controls autoPlay playsInline onClick={(e) => e.stopPropagation()}
            className="max-h-[88vh] w-auto max-w-full aspect-[4/5] rounded-xl bg-black" />
        </div>
      )}
    </>
  );
};
